import type { JourneyMode } from '@/domain/audience/types'

export interface SharedRouteSnapshot {
  mode: JourneyMode
  transportId: string
  originId: string
  gateId: string
  destinationId: string
  destinationIndex?: number
  zoneId: string
}

interface RouteIdentityV1 {
  v: 1
  eventId: string
  mode: JourneyMode
  transportId: string
  gateId: string
  destinationId: string
  zoneId: string
  originId?: string
}

type RouteQuery = Record<string, unknown>

export function createSharedRouteId(snapshot: SharedRouteSnapshot, eventId: string): string {
  const identity: RouteIdentityV1 = {
    v: 1,
    eventId,
    mode: snapshot.mode,
    transportId: snapshot.transportId,
    gateId: snapshot.mode === 'entry' ? snapshot.gateId : '',
    destinationId: snapshot.mode === 'exit' ? snapshot.destinationId : '',
    zoneId: snapshot.mode === 'entry' ? snapshot.zoneId : '',
  }
  if (snapshot.mode === 'entry' && snapshot.originId) identity.originId = snapshot.originId
  return `v1-${encodeBase64Url(JSON.stringify(identity))}`
}

export function createSharedRouteHash(snapshot: SharedRouteSnapshot, eventId: string, releaseId = ''): string {
  const params = new URLSearchParams({
    restorePage: 'summary',
    mode: snapshot.mode,
    transport: snapshot.transportId,
    transportId: snapshot.transportId,
    eventId,
  })
  if (releaseId) params.set('releaseId', releaseId)
  if (snapshot.mode === 'entry') {
    if (snapshot.originId) { params.set('origin', snapshot.originId); params.set('originId', snapshot.originId) }
    if (snapshot.gateId) { params.set('entryGate', snapshot.gateId); params.set('gateId', snapshot.gateId) }
    if (snapshot.zoneId) { params.set('zone', snapshot.zoneId); params.set('zoneId', snapshot.zoneId) }
  } else if (snapshot.destinationId) {
    params.set('destinationId', snapshot.destinationId)
    if (Number.isInteger(snapshot.destinationIndex) && Number(snapshot.destinationIndex) >= 0) params.set('destination', String(snapshot.destinationIndex))
  }
  return `/route/${createSharedRouteId(snapshot, eventId)}?${params.toString()}`
}

export function parseSharedRoute(routeId: string, routeQuery: RouteQuery, currentEventId: string, search = ''): SharedRouteSnapshot | null {
  const query = mergedQuery(routeQuery, search)
  const decoded = decodeRouteId(routeId)
  if (decoded) {
    if (decoded.eventId !== currentEventId || createSharedRouteId({ ...decoded, originId: decoded.originId || '' }, decoded.eventId) !== routeId) return null
    if (!queryMatchesIdentity(query, decoded)) return null
    return {
      mode: decoded.mode,
      transportId: decoded.transportId,
      originId: decoded.originId || '',
      gateId: decoded.gateId,
      destinationId: decoded.destinationId,
      zoneId: decoded.zoneId,
    }
  }
  if (routeId !== 'entry' && routeId !== 'exit') return null
  const mode = routeId
  const transportId = first(query, 'transportId', 'transport')
  const originId = first(query, 'originId', 'origin')
  const gateId = first(query, 'gateId', 'entryGate')
  const zoneId = first(query, 'zoneId', 'zone')
  const destinationId = first(query, 'destinationId')
  const destinationIndex = integer(first(query, 'destination', 'destinationIndex'))
  if (!transportId) return null
  return { mode, transportId, originId, gateId, destinationId, destinationIndex, zoneId }
}

export function legacyRouteFromSearch(search: string): { routeId: JourneyMode; snapshot: SharedRouteSnapshot } | null {
  const query = mergedQuery({}, search)
  if (first(query, 'restorePage') !== 'summary') return null
  const mode = first(query, 'mode')
  if (mode !== 'entry' && mode !== 'exit') return null
  const snapshot = parseSharedRoute(mode, {}, first(query, 'eventId'), search)
  return snapshot ? { routeId: mode, snapshot } : null
}

function decodeRouteId(routeId: string): RouteIdentityV1 | null {
  if (!routeId.startsWith('v1-')) return null
  try {
    const value = JSON.parse(decodeBase64Url(routeId.slice(3))) as Partial<RouteIdentityV1>
    if (value.v !== 1 || (value.mode !== 'entry' && value.mode !== 'exit')) return null
    const fields = [value.eventId, value.transportId, value.gateId, value.destinationId, value.zoneId]
    if (fields.some((item) => typeof item !== 'string') || !value.eventId || !value.transportId) return null
    if (value.originId !== undefined && typeof value.originId !== 'string') return null
    return value as RouteIdentityV1
  } catch { return null }
}

function queryMatchesIdentity(query: Map<string, string>, identity: RouteIdentityV1): boolean {
  const eventId = first(query, 'eventId')
  const mode = first(query, 'mode')
  const transportId = first(query, 'transportId', 'transport')
  const originId = first(query, 'originId', 'origin')
  const gateId = first(query, 'gateId', 'entryGate')
  const zoneId = first(query, 'zoneId', 'zone')
  const destinationId = first(query, 'destinationId')
  return (!eventId || eventId === identity.eventId)
    && (!mode || mode === identity.mode)
    && (!transportId || transportId === identity.transportId)
    && (!originId || originId === (identity.originId || ''))
    && (!gateId || gateId === identity.gateId)
    && (!zoneId || zoneId === identity.zoneId)
    && (!destinationId || destinationId === identity.destinationId)
}

function mergedQuery(routeQuery: RouteQuery, search: string): Map<string, string> {
  const result = new Map<string, string>()
  new URLSearchParams(search).forEach((value, key) => result.set(key, value))
  Object.entries(routeQuery).forEach(([key, value]) => {
    const scalar = Array.isArray(value) ? value[0] : value
    if (typeof scalar === 'string') result.set(key, scalar)
  })
  return result
}

function first(query: Map<string, string>, ...keys: string[]): string {
  for (const key of keys) { const value = query.get(key)?.trim(); if (value) return value }
  return ''
}

function integer(value: string): number | undefined {
  if (!/^(0|[1-9]\d*)$/.test(value)) return undefined
  return Number(value)
}

function encodeBase64Url(value: string): string {
  const bytes = new TextEncoder().encode(value)
  let binary = ''
  bytes.forEach((byte) => { binary += String.fromCharCode(byte) })
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

function decodeBase64Url(value: string): string {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '='))
  return new TextDecoder().decode(Uint8Array.from(binary, (character) => character.charCodeAt(0)))
}
